import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import { hotArticles, categories } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug) || categories[0];
  const filteredArticles = hotArticles.filter((a) => a.categorySlug === slug);

  const allArticles = filteredArticles.length > 0 ? filteredArticles : hotArticles;

  return (
    <>
      <SEO
        title={`${category.name} | 吃瓜网每日大赛`}
        description={`吃瓜网每日大赛${category.name}频道，追踪最新${category.name}动态。实时更新网红塌房、娱乐八卦、黑料大瓜等热门内容。`}
        keywords={`${category.name},吃瓜网每日大赛,吃瓜爆料,网红黑料,实时追踪`}
        canonicalUrl={`https://chigua-daily.com/category/${slug}`}
        schemaType="WebPage"
        breadcrumbs={[
          { name: '首页', url: 'https://chigua-daily.com/' },
          { name: category.name, url: `https://chigua-daily.com/category/${slug}` },
        ]}
      />
      <Navbar />
      <main className="pt-16 md:pt-18">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 py-4">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-foreground-900 transition-colors">首页</Link>
              <span>/</span>
              <span className="text-foreground-900 font-medium">{category.name}</span>
            </nav>
          </div>
        </div>

        {/* Category Header */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl">
            <span className="text-xs text-accent-600 font-medium tracking-wider uppercase">
              {category.name} · {category.count}篇
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-foreground-900 mt-3 leading-tight">
              追踪最新
              <br />
              {category.name}
              <span className="inline-block ml-3 align-middle w-10 h-10 md:w-14 md:h-14 rounded-full overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Dramatic%20breaking%20news%20concept%20art%2C%20warm%20crimson%20and%20gold%20abstract%2C%20editorial%20style%2C%20dark%20textured%20background%2C%20minimalist&width=120&height=120&seq=cat-icon-wanghong&orientation=squarish"
                  alt={category.name}
                  className="w-full h-full object-cover"
                />
              </span>
              <br />
              实时爆料
              <span className="inline-block ml-2 w-8 h-8 md:w-10 md:h-10 rounded-lg overflow-hidden align-middle">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20news%20icon%20concept%2C%20warm%20amber%20tones%2C%20minimalist%20design%2C%20dark%20background&width=100&height=100&seq=cat-icon-small&orientation=squarish"
                  alt="爆料"
                  className="w-full h-full object-cover"
                />
              </span>
            </h1>
            <div className="flex items-center gap-3 mt-6 md:mt-8">
              <Link
                to="/"
                className="px-6 py-2.5 bg-foreground-900 text-background-50 rounded-full text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap"
              >
                返回首页
              </Link>
              <Link
                to={`/category/${slug}`}
                className="px-6 py-2.5 border border-foreground-300 text-foreground-700 rounded-full text-sm font-medium hover:border-foreground-500 transition-colors whitespace-nowrap"
              >
                全部文章
              </Link>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-8 overflow-x-auto scrollbar-hide pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-700 border border-background-200/70 hover:border-primary-300'
                  }`}
                >
                  <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {allArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group block"
                >
                  <article className="rounded-xl overflow-hidden border border-background-200/70 bg-background-50 hover:border-primary-300 transition-all duration-300">
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="text-xs px-2.5 py-1 rounded-full bg-background-50/90 text-foreground-800 font-medium">
                          {article.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 mt-2 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-background-200/70">
                        <span className="text-xs text-foreground-500">
                          {article.author} · {article.publishDate}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-accent-600 font-semibold">
                          <i className="ri-fire-line w-3 h-3 flex items-center justify-center"></i>
                          {article.hotValue.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}